import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights with connection string from environment variables
 * Best practice: Connection string should be stored in environment variables
 * and configured through Azure Static Web App configuration settings
 */
export const initializeAppInsights = (): ReactPlugin | null => {
  // Get connection string from environment variable
  const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

  // Only initialize if connection string is provided
  if (!connectionString) {
    console.warn(
      'Application Insights connection string not found. Telemetry will not be collected.'
    );
    return null;
  }

  try {
    const browserHistory = createBrowserHistory();

    // Create React plugin for automatic page view tracking
    reactPlugin = new ReactPlugin();

    // Initialize Application Insights
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false, // Automatically track route changes
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history: browserHistory },
        },
      },
    });

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer((item) => {
      console.log('Telemetry item:', item);
      if (item.baseType === 'PageviewData' && item.baseData) {
        const url = item.baseData?.uri;

        if (url?.includes('/login')) {
          item.baseData.name = 'Login';
        } else if (url?.includes('/confirm-email')) {
          item.baseData.name = 'Confirm Email';
        } else if (url?.includes('/about')) {
          item.baseData.name = 'About';
        } else if (url?.includes('/sample/chat')) {
          const match = url.match(/\/sample\/chat\/([^/]+)/);
          if (match) {
            const slug = match[1];
            item.baseData.name = `Sample Chat (${slug})`;
          } else {
            item.baseData.name = 'Sample Chat';
          }
        } else if (url?.includes('/sample/devotional')) {
          const match = url.match(/\/sample\/devotional\/([^/]+)/);
          if (match) {
            const slug = match[1];
            item.baseData.name = `Sample Devotional (${slug})`;
          } else {
            item.baseData.name = 'Sample Devotional';
          }
        } else if (url?.includes('/privacy')) {
          item.baseData.name = 'Privacy Policy';
        } else if (url?.includes('/shares/msg')) {
          const match = url.match(/\/shares\/msg\/([^/]+)/);
          if (match) {
            const slug = match[1];
            item.baseData.name = `Shared Message (${slug})`;
          } else {
            item.baseData.name = 'Shared Message';
          }
        } else if (url?.includes('/chat')) {
          item.baseData.name = 'Chat';
        } else if (url?.includes('/profile')) {
          item.baseData.name = 'Profile';
        } else if (url?.includes('/study')) {
          item.baseData.name = 'Study';
        } else if (url?.includes('/devotional')) {
          item.baseData.name = 'Devotional';
        } else if (url?.includes('/favorites')) {
          item.baseData.name = 'Favorites';
        } else if (url === '/') {
          item.baseData.name = 'Home';
        }

        console.log(item.baseData.name);
      }
    });

    return reactPlugin;
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
    return null;
  }
};

/**
 * Get the Application Insights instance for manual tracking
 */
export const getAppInsights = (): ApplicationInsights | null => {
  return appInsights;
};

/**
 * Get the React plugin instance
 */
export const getReactPlugin = (): ReactPlugin | null => {
  return reactPlugin;
};

/**
 * Track exception manually
 */
export const trackException = (error: Error, severityLevel?: number): void => {
  if (appInsights) {
    appInsights.trackException({ exception: error, severityLevel });
  }
};
